import type { RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import type { CalendarMonth } from './Calendar.types'

interface Options {
  /** When false the whole range renders at once. */
  enabled: boolean
  /** Months kept mounted either side of the anchor. */
  renderAhead: number
  /** Index of the month to open on. */
  anchorIndex: number
  scrollContainerRef?: RefObject<HTMLElement | null>
}

interface MonthWindow {
  visibleMonths: Array<CalendarMonth>
  topSentinelRef: (node?: Element | null) => void
  bottomSentinelRef: (node?: Element | null) => void
  hasMonthsAbove: boolean
  hasMonthsBelow: boolean
}

/**
 * Keeps a sliding window of months mounted rather than all eighteen.
 *
 * Prepending a month above the viewport would otherwise shove the content
 * down under the reader, so the scroll position is corrected by the height
 * the prepend added, before paint.
 */
export function useMonthWindow(months: Array<CalendarMonth>, options: Options): MonthWindow {
  const { enabled, renderAhead, anchorIndex, scrollContainerRef } = options

  const [range, setRange] = useState(() => ({
    offset: Math.max(anchorIndex - renderAhead, 0),
    count: renderAhead * 2 + 1,
  }))

  const pendingPrependRef = useRef<number | undefined>(undefined)

  /*
   * The scroller is a parent, so its ref is still empty while this renders.
   * Read straight from the ref the observers would silently fall back to the
   * viewport, and the pre-load margins below would be measured against the
   * page rather than the panel. Holding it in state re-runs them once it is
   * attached.
   */
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | undefined>(undefined)
  useEffect(() => {
    setScrollRoot(scrollContainerRef?.current ?? undefined)
  }, [scrollContainerRef])

  const [topRef, topInView] = useInView({
    root: scrollRoot,
    rootMargin: '20px 0px 0px 0px',
    skip: !enabled,
  })
  const [bottomRef, bottomInView] = useInView({
    root: scrollRoot,
    rootMargin: '0px 0px 100px 0px',
    skip: !enabled,
  })

  /*
   * Re-anchor only when the month asked for is not mounted at all. Anchoring
   * on every change would throw away the months a reader had scrolled through
   * to get somewhere; leaving it pinned to the first render, though, opens the
   * panel months away from a date chosen after it mounted — which is the
   * normal case for a dropdown mounted hidden at page load.
   */
  useLayoutEffect(() => {
    if (!enabled) return
    setRange((current) => {
      const isMounted = anchorIndex >= current.offset && anchorIndex < current.offset + current.count
      if (isMounted) return current
      return { offset: Math.max(anchorIndex - renderAhead, 0), count: renderAhead * 2 + 1 }
    })
  }, [anchorIndex, enabled, renderAhead])

  const hasMonthsAbove = range.offset > 0
  const hasMonthsBelow = range.offset + range.count < months.length

  const extendUpwards = useCallback(() => {
    pendingPrependRef.current = scrollContainerRef?.current?.scrollHeight
    setRange(current => ({ offset: Math.max(current.offset - 1, 0), count: current.count + 1 }))
  }, [scrollContainerRef])

  useLayoutEffect(() => {
    if (topInView && hasMonthsAbove) extendUpwards()
  }, [topInView, hasMonthsAbove, extendUpwards])

  useLayoutEffect(() => {
    if (bottomInView && hasMonthsBelow) {
      setRange(current => ({ ...current, count: current.count + 1 }))
    }
  }, [bottomInView, hasMonthsBelow])

  useLayoutEffect(() => {
    const previousHeight = pendingPrependRef.current
    const container = scrollContainerRef?.current
    if (previousHeight === undefined || !container) return

    pendingPrependRef.current = undefined
    container.scrollTop += container.scrollHeight - previousHeight
  }, [range, scrollContainerRef])

  if (!enabled) {
    return {
      visibleMonths: months,
      topSentinelRef: topRef,
      bottomSentinelRef: bottomRef,
      hasMonthsAbove: false,
      hasMonthsBelow: false,
    }
  }

  /*
   * Clamp before slicing. The range is state and the months are a prop, so a
   * shorter range arriving after the window has scrolled would otherwise slice
   * past the end and mount nothing at all — a blank calendar rather than a
   * short one.
   */
  const offset = Math.max(Math.min(range.offset, months.length - 1), 0)

  return {
    visibleMonths: months.slice(offset, offset + range.count),
    topSentinelRef: topRef,
    bottomSentinelRef: bottomRef,
    hasMonthsAbove,
    hasMonthsBelow,
  }
}
